import { auraTour } from '@modules/_shared/components/chat/auraTour';
import { enquiryResponseTour } from '@modules/_shared/components/listing-marketing/enquiryResponseTour';
import type { Translate } from '@shared/hooks/locale';

import { leadOneClickCmaTour } from '../../lead-details/leadOneClickCmaTour';
import { listingBrochureTour } from '../../listings/listingBrochureTour';
import { listingBuyersTour } from '../../listings/listingBuyersTour';
import { listingWatermarkTour } from '../../listings/listingWatermarkTour';
import {
  mlsBrowseTour,
  mlsBuyersEmailTour,
  mlsShareListingTour,
} from '../../mls/mlsTours';
import { whatsNewBellTour } from '../../notification-inbox/whatsNewBellTour';
import { notificationPreferencesTour } from '../../notifications/notificationPreferencesTour';
import { prospectionMapTour } from '../../prospection-map/prospectionMapTour';
import {
  cmaComparablesCardsTour,
  cmaDvfTransactionsTour,
  cmaRentalComparablesTour,
} from '../../reports/cma-reports/cmaTours';
import { reviewReplyTour } from '../../reviews/reviewReplyTour';
import { profilePhotoTour } from '../../users/user-overview/profilePhotoTour';

import type { TourDefinition, TourModule, TourPathContext } from './tourTypes';

// Every guided tour, by id. A tour lives next to the feature it explains; this
// map is the only place that lists them. Release entries reference these ids
// in their `tour` field (checked by `releaseContent.node-test.ts`).
const tours = {
  'aura-assistant': auraTour,
  'cma-comparables-cards': cmaComparablesCardsTour,
  'cma-dvf-transactions': cmaDvfTransactionsTour,
  'cma-rental-comparables': cmaRentalComparablesTour,
  'enquiry-response': enquiryResponseTour,
  'lead-one-click-cma': leadOneClickCmaTour,
  'listing-brochure': listingBrochureTour,
  'listing-buyers': listingBuyersTour,
  'listing-watermark': listingWatermarkTour,
  'mls-browse': mlsBrowseTour,
  'mls-buyers-email': mlsBuyersEmailTour,
  'mls-share-listing': mlsShareListingTour,
  'notification-preferences': notificationPreferencesTour,
  'profile-photo': profilePhotoTour,
  'prospection-map': prospectionMapTour,
  'reviews-reply': reviewReplyTour,
  'whats-new-bell': whatsNewBellTour,
} satisfies Record<string, TourModule>;

const RESOLVE_PATH_TIMEOUT_MS = 10000;

export type TourId = keyof typeof tours;

export const TOUR_IDS = Object.keys(tours) as TourId[];

export const isTourId = (value: string): value is TourId => value in tours;

export const getTourDefinition = (
  id: TourId,
  t: Translate,
): TourDefinition => ({
  id,
  steps: tours[id].steps(t),
  startOnAnyStep: (tours[id] as TourModule).startOnAnyStep === true,
});

// Whether the tour looks up its screen per user (else it plays anywhere its
// elements are, e.g. the bell).
export const hasTourPath = (id: TourId): boolean =>
  (tours[id] as TourModule).resolvePath != null;

export const resolveTourPath = async (
  id: TourId,
  context: TourPathContext,
): Promise<string | null> => {
  const { resolvePath } = tours[id] as TourModule;
  if (resolvePath == null) {
    return null;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>(resolve => {
    timer = setTimeout(() => resolve(null), RESOLVE_PATH_TIMEOUT_MS);
  });
  try {
    return await Promise.race([resolvePath(context), timeout]);
  } catch {
    // A failed lookup only costs the deep link: fall back to the entry path.
    return null;
  } finally {
    clearTimeout(timer);
  }
};
